import { FormEvent, useState } from "react";
import { toast } from "sonner";

import { api } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { navigate } from "../../lib/router";
import { useLanguage } from "../../contexts/LanguageContext";
import { clientsLabels } from "./labels";
import { ChevronLeft } from "../../components/icons";

export default function NewClientPage() {
  const { lang } = useLanguage();
  const formLabels = clientsLabels[lang].form;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [cpf, setCpf] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) return;
    setError(null);

    const trimmedName = name.trim();
    if (!trimmedName) {
      setError(formLabels.requiredName);
      return;
    }

    setSaving(true);
    try {
      const response = await api.createClient({
        name: trimmedName,
        email: email.trim() || null,
        phone: phone.trim() || null,
        cpf: cpf.trim() || null,
      });
      if (!response.ok) {
        setError(response.error || formLabels.genericError);
        return;
      }
      toast.success(formLabels.success);
      navigate("/app/clients");
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => {
    if (saving) return;
    navigate("/app/clients");
  };

  const handleCancel = () => {
    goBack();
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-4">
      <div className="flex items-start gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={formLabels.back}
          onClick={goBack}
          disabled={saving}
          icon={<ChevronLeft className="h-5 w-5" />}
        />
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold">{formLabels.title}</h1>
          {formLabels.description && (
            <p className="text-sm text-neutral-600">{formLabels.description}</p>
          )}
        </div>
      </div>
      <form onSubmit={handleSubmit} className="rounded-lg border bg-white p-6 shadow-sm">
        <div className="space-y-6">
          <div className="space-y-3">
            <div>
              <Label htmlFor="client-name">{formLabels.name}</Label>
              <Input
                id="client-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="client-cpf">{formLabels.cpf}</Label>
                <Input
                  id="client-cpf"
                  value={cpf}
                  onChange={(event) => setCpf(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="client-phone">{formLabels.phone}</Label>
                <Input
                  id="client-phone"
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </div>
            </div>
            <div>
              <Label htmlFor="client-email">{formLabels.email}</Label>
              <Input
                id="client-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>
          </div>
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3 border-t pt-3">
            <Button type="button" variant="ghost" onClick={handleCancel} disabled={saving}>
              {formLabels.cancel}
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? formLabels.saving : formLabels.submit}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
